import { useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import ComboInput from '../common/ComboInput';
import AccessExpiryConfig from '../common/AccessExpiryConfig';
import StringListEditor from './StringListEditor';
import CoursePaymentMethods from './CoursePaymentMethods';

// Lazy so the heavy TipTap editor bundle is never on the static path of public pages.
const RichTextEditor = lazy(() => import('../common/RichTextEditor'));
import { SUPPORTED_CURRENCIES } from '../../utils/currency';
import { COMMON_TIMEZONES, timezoneLabel } from '../../utils/timezones';
import { CourseBasics, PaymentCapabilities, MAX_LEARNING_OUTCOMES, MAX_LEARNING_OUTCOME_LENGTH } from './types';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

interface BasicsFormProps {
  value: CourseBasics;
  onChange: (patch: Partial<CourseBasics>) => void;
  existingCategories: string[];
  paymentCapabilities: PaymentCapabilities | null;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold text-sm';

export default function BasicsForm({ value, onChange, existingCategories, paymentCapabilities }: BasicsFormProps) {
  const { t } = useTranslation('teaching');
  // A loaded course whose slug isn't the auto "untitled-course-…" placeholder counts as
  // manually edited, so we stop overwriting it from the title.
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(
    () => !!value.slug && !value.slug.startsWith('untitled-course-')
  );

  function handleTitleChange(title: string) {
    onChange(slugManuallyEdited ? { title } : { title, slug: slugify(title) });
  }

  return (
    <div className="glass-card rounded-lg p-5 max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('courseEdit.courseDetails')}</h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">{t('courseEdit.titleLabel')}</label>
          <input type="text" value={value.title} onChange={(e) => handleTitleChange(e.target.value)} className={inputCls} required />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">{t('courseEdit.urlSlug')}</label>
          <input
            type="text"
            value={value.slug}
            onChange={(e) => { setSlugManuallyEdited(true); onChange({ slug: e.target.value }); }}
            className={inputCls}
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            title={t('courseEdit.slugPattern')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">{t('courseEdit.descriptionLabel')}</label>
          <Suspense fallback={<div className="border border-gray-200 rounded-md p-3 text-sm text-gray-400 min-h-[180px]">…</div>}>
            <RichTextEditor
              value={value.description}
              onChange={(html) => onChange({ description: html })}
              placeholder={t('courseEdit.descriptionPlaceholder', { defaultValue: '' })}
            />
          </Suspense>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <label className="block text-sm font-medium text-gray-600 mb-1">{t('courseEdit.learningOutcomes')}</label>
          <p className="text-xs text-gray-400 mb-2">{t('courseEdit.learningOutcomesHint')}</p>
          <StringListEditor
            items={value.learningOutcomes}
            onChange={(items) => onChange({ learningOutcomes: items })}
            max={MAX_LEARNING_OUTCOMES}
            maxLength={MAX_LEARNING_OUTCOME_LENGTH}
            addLabel={t('courseEdit.addLearningOutcome')}
            placeholder={t('courseEdit.learningOutcomePlaceholder')}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('courseEdit.category')}</label>
            <ComboInput value={value.category} onChange={(v) => onChange({ category: v })} options={existingCategories} placeholder={t('courseEdit.categoryPlaceholder')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('courseEdit.duration')}</label>
            <input type="text" value={value.duration} onChange={(e) => onChange({ duration: e.target.value })} className={inputCls} placeholder={t('courseEdit.durationPlaceholder')} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('courseEdit.price')}</label>
            <div className="flex gap-1">
              <input
                type="number"
                value={value.price}
                onChange={(e) => { const v = Number(e.target.value); onChange({ price: v === 0 ? 0 : Math.max(0.01, v) }); }}
                className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold text-sm"
                min="0"
                step="0.01"
              />
              <select value={value.currency} onChange={(e) => onChange({ currency: e.target.value })} className="px-2 py-2 border border-gray-200 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gold text-sm">
                {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('courseEdit.startDate')}</label>
            <div className="flex gap-1">
              <input
                type="date"
                value={value.startDate ? value.startDate.split('T')[0] : ''}
                onChange={(e) => onChange({ startDate: e.target.value })}
                className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold text-sm"
              />
              {value.startDate && (
                <button type="button" onClick={() => onChange({ startDate: '' })} className="px-2 py-2 text-gray-400 hover:text-red-500 transition-colors" title="Clear start date">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {value.startDate && (
              <select value={value.startDateTimezone} onChange={(e) => onChange({ startDateTimezone: e.target.value })} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gold text-xs">
                {COMMON_TIMEZONES.map((tz) => <option key={tz} value={tz}>{timezoneLabel(tz)}</option>)}
              </select>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <AccessExpiryConfig
            value={{ accessDurationDays: value.accessDurationDays, accessExpiresAt: value.accessExpiresAt }}
            onChange={(next) => onChange({ accessDurationDays: next.accessDurationDays, accessExpiresAt: next.accessExpiresAt })}
          />
        </div>

        {paymentCapabilities?.customPaymentEnabled && (
          <CoursePaymentMethods value={value} onChange={onChange} capabilities={paymentCapabilities} />
        )}
      </div>
    </div>
  );
}
